import React, { useMemo } from 'react';
import { Tag, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { CargoItemStatusType } from 'api/swagger';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses.ts';
import { findWarehouseById } from 'shared/utils/warehouses/find-warehouse-by-id.ts';
import styles from './cargo-item-status.module.scss';

interface ICargoItemStatus {
  type: CargoItemStatusType;
  from: number | null;
  to: number | null;
  count?: number;
  oneStatus?: boolean;
  assetIsReturned?: boolean;
}

export const CargoItemStatus: React.FC<ICargoItemStatus> = (props) => {
  const { type, from, to, count, oneStatus = false, assetIsReturned } = props;
  const { data: warehouses } = useGetManyWarehouses();

  const getTagProps = (): { color?: string; className?: string; icon: React.ReactNode } => {
    switch (type) {
      case 'waiting':
        return { color: '', className: styles.tag, icon: <ClockCircleOutlined /> };
      case 'planned':
        return { className: styles.plannedTag, icon: <CalendarOutlined /> };
      case 'warehouse':
        return { className: styles.warehouseTag, icon: <AppstoreOutlined /> };
      case 'loaded':
        return { className: styles.loadedTag, icon: <SyncOutlined /> };
      case 'delivered':
        return {
          color: assetIsReturned === false ? 'orange' : 'green',
          className: styles.tag,
          icon: <CheckCircleOutlined />,
        };
    }
  };

  const getTagText = () => {
    const warehouseFrom = findWarehouseById(from, warehouses);
    const warehouseTo = findWarehouseById(to, warehouses);

    switch (type) {
      case 'waiting':
      case 'delivered':
        return type;
      case 'planned':
      case 'loaded':
        return (
          <>
            {warehouseFrom?.name || <UserOutlined />}
            <ArrowRightOutlined className={styles.arrow} />
            {warehouseTo?.name || <UserOutlined />}
          </>
        );
      case 'warehouse':
        return `in ${warehouseFrom?.name}`;
    }
  };

  const getCount = () => {
    if (!count || oneStatus) return null;
    return <span className={styles.countBadge}>{count}</span>;
  };

  const tagProps = useMemo(getTagProps, [type, assetIsReturned]);
  const tagText = useMemo(getTagText, [type, from, to, warehouses]);
  const tagCount = useMemo(getCount, [count, oneStatus]);

  return (
    <Tooltip title={type}>
      <Tag {...tagProps}>
        {tagText}
        {tagCount}
      </Tag>
    </Tooltip>
  );
};
